/**
 * BillingFeePlans — admin CRUD for fee plans (recurring + drop-in + one-time + addons).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";
import { money } from "./BillingDashboard";

const KINDS = ["tuition", "registration", "deposit", "meals", "transport", "extended_hours", "supplies", "field_trip", "addon", "other"];
const FREQS = ["monthly", "weekly", "termly", "annual", "one_time", "hourly", "daily"];

const EMPTY_ITEM = {
  kind: "tuition",
  label: "",
  amount: 0,
  frequency: "monthly",
  taxable: true,
  required: true,
  description: "",
};

export default function BillingFeePlans() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/billing/fee-plans");
      setPlans(r.data || []);
    } catch (e) {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const startNew = () =>
    setEditing({
      name: "",
      description: "",
      currency: "AED",
      items: [{ ...EMPTY_ITEM }],
      addons: [],
      _new: true,
    });
  const startEdit = (plan) => setEditing({ ...plan, _new: false });

  const submit = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Plan needs a name.");
      return;
    }
    const body = {
      name: editing.name.trim(),
      description: editing.description || null,
      currency: editing.currency || "AED",
      items: (editing.items || []).map((i) => ({
        kind: i.kind,
        label: i.label,
        amount: Number(i.amount),
        frequency: i.frequency,
        taxable: !!i.taxable,
        required: i.required !== false,
        description: i.description || null,
      })),
      addons: (editing.addons || []).map((i) => ({
        kind: i.kind || "addon",
        label: i.label,
        amount: Number(i.amount),
        frequency: i.frequency,
        taxable: !!i.taxable,
        required: false,
        description: i.description || null,
      })),
    };
    try {
      if (editing._new) {
        await api.post("/admin/billing/fee-plans", body);
      } else {
        await api.patch(`/admin/billing/fee-plans/${editing.id}`, body);
      }
      toast.success(t("billing.messages.fee_plan_saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("billing.fee_plans.delete_confirm"))) return;
    try {
      await api.delete(`/admin/billing/fee-plans/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-billing-fee-plans">
      <AdminHeader
        eyebrow={t("billing.fee_plans.eyebrow")}
        title={t("billing.fee_plans.title")}
        action={
          <button
            onClick={startNew}
            data-testid="billing-fee-plan-new"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Plus size={14} strokeWidth={2} />
            {t("billing.fee_plans.create_cta")}
          </button>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("billing.fee_plans.blurb")}</p>

      {loading ? (
        <p className="font-serif italic text-[#525252]">{t("billing.common.loading")}</p>
      ) : plans.length === 0 && !editing ? (
        <p className="font-serif italic text-[#9CA3AF]" data-testid="billing-fee-plans-empty">
          {t("billing.fee_plans.empty_state")}
        </p>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {plans.map((p) => (
            <li
              key={p.id}
              data-testid={`billing-fee-plan-${p.id}`}
              className="rounded-2xl bg-white border border-[#EEF0F4] p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg text-[#1A1B2E]">{p.name}</h3>
                  {p.description && <p className="text-sm text-[#6B7280] mt-1">{p.description}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`billing-fee-plan-edit-${p.id}`}
                    className="text-xs text-[#6B7280] hover:text-[#1A1B2E] inline-flex items-center gap-1"
                  >
                    <Pencil size={12} strokeWidth={1.6} /> edit
                  </button>
                  <button
                    onClick={() => del(p.id)}
                    data-testid={`billing-fee-plan-delete-${p.id}`}
                    className="text-xs text-[#A11D1D] hover:text-[#8B1818] inline-flex items-center gap-1"
                  >
                    <Trash2 size={12} strokeWidth={1.6} /> delete
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {(p.items || []).map((it) => (
                  <div key={it.id} className="flex items-center justify-between text-sm border-t border-[#EEF0F4] pt-2">
                    <span className="text-[#1A1B2E]">{it.label}</span>
                    <span className="tabular-nums text-[#6B7280]">
                      {money(it.amount, p.currency)} · {t(`billing.frequency.${it.frequency}`)}
                    </span>
                  </div>
                ))}
                {(p.addons || []).map((it) => (
                  <div key={it.id} className="flex items-center justify-between text-sm border-t border-dashed border-[#EEF0F4] pt-2 text-[#9CA3AF] italic">
                    + {it.label}
                    <span className="tabular-nums">
                      {money(it.amount, p.currency)} · {t(`billing.frequency.${it.frequency}`)}
                    </span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="billing-fee-plan-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl text-[#1A1B2E]">
              {editing._new ? t("billing.fee_plans.create_cta") : "Edit fee plan"}
            </h3>
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="eyebrow">{t("billing.fee_plans.name_label")}</span>
                <input
                  type="text"
                  data-testid="billing-fee-plan-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="block">
                <span className="eyebrow">{t("billing.fee_plans.description_label")}</span>
                <textarea
                  rows={2}
                  data-testid="billing-fee-plan-description"
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="block max-w-[120px]">
                <span className="eyebrow">{t("billing.fee_plans.currency_label")}</span>
                <input
                  type="text"
                  maxLength={4}
                  data-testid="billing-fee-plan-currency"
                  value={editing.currency || "AED"}
                  onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm tracking-widest focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>

              <Section
                title={t("billing.fee_plans.items_title")}
                items={editing.items || []}
                onChange={(items) => setEditing({ ...editing, items })}
                addCta={t("billing.fee_plans.add_item_cta")}
                testidPrefix="billing-plan-item"
              />
              <Section
                title={t("billing.fee_plans.addons_title")}
                items={editing.addons || []}
                onChange={(addons) => setEditing({ ...editing, addons })}
                addCta={t("billing.fee_plans.add_addon_cta")}
                testidPrefix="billing-plan-addon"
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setEditing(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">
                {t("billing.common.cancel")}
              </button>
              <button
                onClick={submit}
                data-testid="billing-fee-plan-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
              >
                {t("billing.fee_plans.save_plan")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function Section({ title, items, onChange, addCta, testidPrefix }) {
  const { t } = useI18n();
  const add = () => onChange([...items, { ...EMPTY_ITEM }]);
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="border border-[#EEF0F4] rounded-2xl p-4 bg-[#FAF9F1]/30">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{title}</div>
        <button
          type="button"
          onClick={add}
          data-testid={`${testidPrefix}-add`}
          className="text-xs text-[#1F3A2A] hover:underline inline-flex items-center gap-1"
        >
          <Plus size={12} strokeWidth={1.8} /> {addCta}
        </button>
      </div>
      <ul className="mt-3 space-y-3">
        {items.map((it, idx) => (
          <li key={idx} className="rounded-xl bg-white border border-[#EEF0F4] p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
            <select
              value={it.kind}
              onChange={(e) => update(idx, { kind: e.target.value })}
              className="sm:col-span-3 px-2 py-1.5 border border-[#EEF0F4] rounded text-sm bg-white"
              data-testid={`${testidPrefix}-${idx}-kind`}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>{t(`billing.kind.${k}`)}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Label"
              value={it.label}
              onChange={(e) => update(idx, { label: e.target.value })}
              className="sm:col-span-3 px-2 py-1.5 border border-[#EEF0F4] rounded text-sm"
              data-testid={`${testidPrefix}-${idx}-label`}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={it.amount}
              onChange={(e) => update(idx, { amount: e.target.value })}
              className="sm:col-span-2 px-2 py-1.5 border border-[#EEF0F4] rounded text-sm tabular-nums"
              data-testid={`${testidPrefix}-${idx}-amount`}
            />
            <select
              value={it.frequency}
              onChange={(e) => update(idx, { frequency: e.target.value })}
              className="sm:col-span-2 px-2 py-1.5 border border-[#EEF0F4] rounded text-sm bg-white"
              data-testid={`${testidPrefix}-${idx}-frequency`}
            >
              {FREQS.map((f) => (
                <option key={f} value={f}>{t(`billing.frequency.${f}`)}</option>
              ))}
            </select>
            <label className="sm:col-span-1 inline-flex items-center gap-1 text-xs text-[#6B7280]">
              <input
                type="checkbox"
                checked={it.taxable !== false}
                onChange={(e) => update(idx, { taxable: e.target.checked })}
                data-testid={`${testidPrefix}-${idx}-taxable`}
              />
              tax
            </label>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="sm:col-span-1 text-xs text-[#A11D1D] hover:underline"
              data-testid={`${testidPrefix}-${idx}-remove`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-xs italic text-[#9CA3AF]">No items yet.</li>
        )}
      </ul>
    </div>
  );
}
